import Layout from "../components/Layout";
import { Link } from "@tanstack/react-router";
import { Award, MapPin, Shield, Star, Users } from "lucide-react";
import { motion } from "motion/react";

// ─── Data ──────────────────────────────────────────────────────────────────────

const instructors = [
  {
    name: "Mike DeWitt Jr.",
    profileImage: "/assets/images/Superhero Jiu-Jitsu Acadmey_Mike Dewitt.jpg",
    title: "Head Instructor",
    belt: "Black Belt",
    beltColor: "bg-foreground text-background",
    details: [
      "Wrestling background",
      "Multiple-time competition champion",
      "Proven coaching system focused on real-world performance",
    ],
  },
  {
    name: "Zach Borrelli",
    profileImage: "/assets/images/Superhero Jiu-Jitsu Acadmey_Zach Borrelli.jpg",
    title: "Instructor",
    belt: "Black Belt",
    beltColor: "bg-foreground text-background",
    details: [],
  },
  {
    name: "Kevin Boehm",
    profileImage: "/assets/images/Superhero Jiu-Jitsu Acadmey_Kevin Boehm.jpg",
    title: "Instructor",
    belt: "Brown Belt",
    beltColor: "bg-amber-800 text-white",
    details: [],
  },
  {
    name: "Sarah Gabby Keim",
    profileImage: "/assets/images/Superhero Jiu-Jitsu Acadmey_Sarah Gabby Keim.jpg",
    title: "Instructor",
    belt: "Brown Belt",
    beltColor: "bg-amber-800 text-white",
    details: [],
  },
];

const facilities = [
  "2,000 sq ft of mat space",
  "Two training rooms",
  "Strength and cardio equipment",
  "Kids area",
  "On-site parking",
];

const serviceAreas = [
  "Springfield",
  "Eugene",
  "Thurston",
  "Glenwood",
  "Fairmont",
  "Lane County",
];

const partnerships = [
  {
    icon: Award,
    heading: "Community Recognition",
    body: "In 2024, Superhero Jiu Jitsu Academy was selected for a Community Choice Award in the Eugene area. This recognition reflects the trust and support of our students, families, and local community. We remain committed to delivering a high-level training environment that makes a lasting impact.",
  },
  {
    icon: Shield,
    heading: "Supporting Veterans Through Training",
    body: "We are a proud affiliate of the We Defy Foundation, helping combat veterans train, rebuild, and reconnect through Jiu Jitsu. Our academy provides a structured, supportive space where veterans can develop both physically and mentally alongside a strong community.",
  },
  {
    icon: Users,
    heading: "Law Enforcement Training Partnership",
    body: "Superhero Jiu Jitsu Academy is affiliated with C4C Police Jiu Jitsu, supporting training focused on control, de-escalation, and real-world application. We host and deliver curriculum designed to help professionals perform with confidence and accountability under pressure.",
  },
];

// ─── Belt Badge ────────────────────────────────────────────────────────────────

function BeltBadge({
  belt,
  colorClass,
}: {
  belt: string;
  colorClass: string;
}) {
  return (
    <span
      className={`inline-block text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${colorClass}`}
    >
      {belt}
    </span>
  );
}

// ─── Instructor Card ───────────────────────────────────────────────────────────

function InstructorCard({
  instructor,
  index,
}: {
  instructor: (typeof instructors)[number];
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.1 }}
      className="bg-card border border-border rounded-2xl shadow-card overflow-hidden flex flex-col"
    >
      {/* image */}
      {/* Avatar area */}
      <div className="bg-primary px-6 py-6 flex flex-col items-center text-center gap-3">
        
        <img src={instructor.profileImage} className="w-16 h-16 rounded-full flex items-center justify-center shadow-gold flex-shrink-0"
                loading="lazy"/>
        <div>
          <div className="font-display font-black text-base text-primary-foreground tracking-tight leading-tight">
            {instructor.name}
          </div>
          <div className="text-xs text-primary-foreground/60 font-body mt-0.5">
            {instructor.title}
          </div>
        </div>
        <BeltBadge belt={instructor.belt} colorClass={instructor.beltColor} />
      </div>

      {/* Details */}
      {instructor.details.length > 0 && (
        <div className="px-6 py-4 flex-1">
          <ul className="space-y-2">
            {instructor.details.map((d) => (
              <li
                key={d}
                className="flex items-start gap-2 text-sm font-body text-foreground/80"
              >
                <Star className="w-3.5 h-3.5 text-gold mt-0.5 flex-shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function AboutPage() {
  return (
    <Layout>
      <title>About Superhero Jiu Jitsu Academy | Springfield, OR</title>
      <meta
        name="description"
        content="Superhero Jiu Jitsu Academy is the longest-running accredited BJJ school in Lane County. Join our community and discover the confidence, friendships, and self-defense skills that Jiu-Jitsu can add to your life."
      />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section
        className="relative bg-hero overflow-hidden py-16 md:py-24"
        aria-labelledby="about-hero-heading"
      >
        <div className="absolute inset-0 opacity-5" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, var(--color-accent) 0, var(--color-accent) 1px, transparent 0, transparent 50%)",
              backgroundSize: "20px 20px",
            }}
          />
        </div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-3xl mx-auto"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
              Lane County's Premier BJJ Academy
            </span>
            <h1
              id="about-hero-heading"
              className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight text-balance mb-5"
            >
              About Superhero Jiu Jitsu Academy
            </h1>
            <p className="text-hero-foreground text-base sm:text-lg leading-relaxed max-w-2xl mx-auto opacity-80">
              The longest-running accredited BJJ school in Lane County — built
              on community, confidence, and real-world results since 2014.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── ABOUT CONTENT ────────────────────────────────────────────────── */}
      <section
        className="bg-background py-16 md:py-20"
        aria-labelledby="about-content-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
                Our Story
              </span>
              <h2
                id="about-content-heading"
                className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance mb-5"
              >
                Built for Community. Proven in Competition.
              </h2>
              <div className="space-y-4 text-foreground/80 font-body leading-relaxed">
                <p>
                  Superhero Jiu Jitsu Academy was founded in 2014 to create a
                  welcoming, family-friendly training environment for
                  Springfield and surrounding communities.
                </p>
                <p>
                  The academy offers programs for kids, teens, and adults, with
                  classes scheduled throughout the day to fit busy lifestyles.
                </p>
              </div>

              {/* Service areas */}
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
                  <span className="text-sm font-display font-semibold tracking-widest uppercase text-foreground">
                    Serving Our Community
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {serviceAreas.map((area) => (
                    <span
                      key={area}
                      className="text-xs font-body font-medium px-3 py-1 rounded-full bg-muted text-foreground border border-border"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Facility card */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-card border border-border rounded-2xl shadow-card overflow-hidden"
            >
              <div className="h-1 bg-gradient-accent" />
              <div className="p-6">
                <h3 className="font-display font-black text-lg text-foreground mb-4 tracking-tight">
                  Our Facility
                </h3>
                <ul className="space-y-3">
                  {facilities.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-3 font-body text-sm text-foreground/80"
                    >
                      <span className="w-2 h-2 rounded-full bg-gradient-accent flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── INSTRUCTORS ──────────────────────────────────────────────────── */}
      <section
        className="bg-muted/30 py-16 md:py-20 border-y border-border"
        aria-labelledby="instructors-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
              Coaching Staff
            </span>
            <h2
              id="instructors-heading"
              className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance"
            >
              Meet Our Instructors
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {instructors.map((instructor, i) => (
              <InstructorCard
                key={instructor.name}
                instructor={instructor}
                index={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── COMMUNITY & PARTNERSHIPS ─────────────────────────────────────── */}
      <section
        className="bg-background py-16 md:py-20"
        aria-labelledby="community-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
              Beyond the Mat
            </span>
            <h2
              id="community-heading"
              className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance"
            >
              Community &amp; Partnerships
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {partnerships.map(({ icon: Icon, heading, body }, i) => (
              <motion.div
                key={heading}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="bg-card border border-border rounded-2xl shadow-card overflow-hidden"
              >
                <div className="h-1 bg-gradient-accent" />
                <div className="p-6 flex flex-col gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-display font-black text-base text-foreground tracking-tight leading-snug">
                    {heading}
                  </h3>
                  <p className="text-sm font-body text-foreground/75 leading-relaxed">
                    {body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section
        className="bg-hero relative overflow-hidden py-20"
        aria-labelledby="about-cta-heading"
      >
        <div className="absolute inset-0 opacity-5" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, var(--color-accent) 0, var(--color-accent) 1px, transparent 0, transparent 50%)",
              backgroundSize: "20px 20px",
            }}
          />
        </div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
              Limited Spots Available
            </span>
            <h2
              id="about-cta-heading"
              className="font-display font-black text-4xl sm:text-5xl text-hero-foreground mb-4 text-balance"
            >
              Start Your Jiu Jitsu Journey Today
            </h2>
            <p className="text-hero-foreground text-lg mb-8 leading-relaxed opacity-80">
              Your first class is completely free — no experience needed, no
              commitment required. Come see what Superhero Jiu Jitsu Academy is
              all about.
            </p>
            <Link
              to="/contact-us"
              data-ocid="about-bottom-cta"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-8 py-4 text-base font-display font-black uppercase tracking-widest text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Claim Your Free Class
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
